import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { SrCustomerStatus } from '@plato/data/starrocks/types';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import type { SearchEntityItemData } from '@plato/search/search-types';
import { GlobalSearchTab, SearchEntity } from '@plato/search/search-types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { formatOrderId } from '../../../lib/order-helpers.ts';
import { api } from '../../../utils/api.ts';
import { safeJoin } from '../../../utils/string-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { SearchBasePayload, SearchResultItem } from '../../analytics/types/analytics-events.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import type { SearchHighlight } from '../utils/highlighting.ts';
import {
    selectCustomerHighlightsToDisplay,
    selectProductHighlightsToDisplay,
    selectSalesOrderHighlightsToDisplay,
} from '../utils/highlighting.ts';

type SearchEntityKeys = keyof SearchEntityItemData;

/** Icon names the result row knows how to render for a meta field. */
export type SearchFieldIcon = 'id' | 'location' | 'customer';

/** A secondary meta field, optionally prefixed with an icon in the result row. */
export type SearchMetaField = {
    text: string;
    icon?: SearchFieldIcon;
};

export type SearchEntityItem<T extends SearchEntityKeys = SearchEntityKeys> = {
    entity: SearchEntity;
    id: string;
    name: string;
    caption?: SearchMetaField;
    subtitle?: SearchMetaField;
    status?: SrCustomerStatus;
    original: SearchEntityItemData[T];
    highlight?: Partial<Record<'name' | 'caption' | 'subtitle' | 'match', SearchHighlight>>;
};

export type SearchTracking = Pick<
    SearchBasePayload,
    'context' | 'customer_id' | 'context_entity_type' | 'context_entity_id'
>;

function metaField(text: string | undefined, icon: SearchFieldIcon): SearchMetaField | undefined {
    return text ? { text, icon } : undefined;
}

type UseSearchEntityDataProps = {
    entities: SearchEntity[];
    search: string;
    selectedTabKey: GlobalSearchTab;
    tracking: SearchTracking;
};

type UseSearchEntityDataReturn = {
    data: Map<SearchEntity, SearchEntityItem[]>;
    version: string | undefined;
    requestIds: Partial<Record<SearchEntity, string>> | undefined;
    dataUpdatedAt: number;
    limit: number;
    offset: number;
    isLoading: boolean;
    isFetched: boolean;
    trackResultClicked: (item: SearchEntityItem) => void;
};

export function getFilteredEntitiesByTab(
    selectedTabKey: GlobalSearchTab,
    entities: SearchEntity[],
): SearchEntity[] {
    switch (selectedTabKey) {
        case GlobalSearchTab.All:
            return entities;
        case GlobalSearchTab.Customers:
            return [SearchEntity.Customer];
        case GlobalSearchTab.Orders:
            return [SearchEntity.SalesOrder];
        case GlobalSearchTab.Quotes:
            return [SearchEntity.SalesQuote];
        case GlobalSearchTab.Products:
            return [SearchEntity.Product];
        default:
            return [];
    }
}

export function useSearchEntityData({
    entities,
    search,
    selectedTabKey,
    tracking,
}: UseSearchEntityDataProps): UseSearchEntityDataReturn {
    const appConfig = useAppConfig();

    const filteredEntities: SearchEntity[] = getFilteredEntitiesByTab(selectedTabKey, entities);
    const limit = selectedTabKey === GlobalSearchTab.All ? 6 : 20;
    const offset = 0;

    const queryInput = { search, limit, entities: filteredEntities };

    const {
        data,
        isLoading: isLoadingQuery,
        isFetched: isFetchedQuery,
        dataUpdatedAt,
    } = api.platoUser.search.searchViaApi.useQuery(queryInput, {
        enabled: Boolean(search),
    });

    const filteredItems = data?.items ?? [];

    const customers = filteredItems.filter(
        (item): item is SearchEntityItemData['customer'] => item.entity === SearchEntity.Customer,
    );

    const orders = filteredItems.filter(
        (item): item is SearchEntityItemData['sales_order'] => item.entity === SearchEntity.SalesOrder,
    );

    const quotes = filteredItems.filter(
        (item): item is SearchEntityItemData['sales_quote'] => item.entity === SearchEntity.SalesQuote,
    );

    const products = filteredItems.filter(
        (item): item is SearchEntityItemData['product'] => item.entity === SearchEntity.Product,
    );

    const toSalesOrderItem = (
        row: SearchEntityItemData['sales_order'] | SearchEntityItemData['sales_quote'],
        entity: 'sales_order' | 'sales_quote',
    ): SearchEntityItem => {
        const {
            fields: { ERP_ID, CUSTOMER_ID, CUSTOMER_NAME },
            extra,
        } = selectSalesOrderHighlightsToDisplay(row);
        const name = formatOrderId(appConfig, row.erp_id);

        return {
            entity,
            id: row.erp_id,
            name,
            subtitle: metaField(safeJoin([row.customer_id, row.customer_name], ' '), 'customer'),
            original: row,
            highlight: {
                name: ERP_ID,
                subtitle:
                    CUSTOMER_ID || CUSTOMER_NAME
                        ? {
                              text: safeJoin(
                                  [
                                      CUSTOMER_ID?.text ?? row.customer_id,
                                      CUSTOMER_NAME?.text ?? row.customer_name,
                                  ],
                                  ' ',
                              ),
                          }
                        : undefined,
                match: extra,
            },
        };
    };

    const dataInOrder = new Map<SearchEntity, SearchEntityItem[]>();

    for (const entity of filteredEntities) {
        switch (entity) {
            case SearchEntity.Customer: {
                dataInOrder.set(
                    SearchEntity.Customer,
                    customers.map((customer) => {
                        const {
                            fields: { NAME, ERP_ID, 'ADDRESS.CITY': highlightedCity },
                            extra,
                        } = selectCustomerHighlightsToDisplay(customer);

                        const createSubname = (city: string | undefined): string =>
                            safeJoin([city, customer.country_code], ', ');

                        const subNameHighlightText = createSubname(highlightedCity?.text ?? customer.city);

                        return {
                            entity: SearchEntity.Customer,
                            id: customer.id,
                            name: customer.name,
                            caption: metaField(customer.erp_id, 'id'),
                            subtitle: metaField(createSubname(customer.city), 'location'),
                            status: customer.status,
                            original: customer,
                            highlight: {
                                name: NAME,
                                caption: ERP_ID,
                                subtitle: { text: subNameHighlightText },
                                // the leftover match, shown between name and address
                                match: extra,
                            },
                        };
                    }),
                );
                break;
            }
            case SearchEntity.Product: {
                dataInOrder.set(
                    SearchEntity.Product,
                    products.map((product) => {
                        const {
                            fields: { PRODUCT_VARIANT_NAME, PRODUCT_VARIANT_ID, PRODUCT_DISPLAY_ID },
                            extra,
                        } = selectProductHighlightsToDisplay(product);

                        return {
                            entity: SearchEntity.Product,
                            id: product.product_variant_id,
                            name: product.product_variant_name,
                            caption: metaField(product.product_variant_id, 'id'),
                            original: product,
                            highlight: {
                                name: PRODUCT_VARIANT_NAME,
                                caption: PRODUCT_VARIANT_ID ?? PRODUCT_DISPLAY_ID,
                                // the leftover match (supplier, GTIN, …), shown between name and id
                                match: extra,
                            },
                        };
                    }),
                );

                break;
            }
            case SearchEntity.SalesOrder: {
                dataInOrder.set(
                    SearchEntity.SalesOrder,
                    orders.map((order) => toSalesOrderItem(order, SearchEntity.SalesOrder)),
                );
                break;
            }
            case SearchEntity.SalesQuote: {
                dataInOrder.set(
                    SearchEntity.SalesQuote,
                    quotes.map((quote) => toSalesOrderItem(quote, SearchEntity.SalesQuote)),
                );
                break;
            }
        }
    }

    const version = data?.version;
    const requestIds = data?.request_ids;

    const sendEvent = useAnalytics();

    const contextIdRef = useRef<string | undefined>(undefined);
    if (!contextIdRef.current) {
        contextIdRef.current = uuidv4();
    }
    const contextId = contextIdRef.current;

    const trackingContext = useMemo<SearchBasePayload>(
        () => ({
            context: tracking.context,
            context_id: contextId,
            customer_id: tracking.customer_id,
            context_entity_type: tracking.context_entity_type,
            context_entity_id: tracking.context_entity_id,
            target_entity_types: getFilteredEntitiesByTab(selectedTabKey, entities),
        }),
        [
            contextId,
            tracking.context,
            tracking.customer_id,
            tracking.context_entity_type,
            tracking.context_entity_id,
            entities,
            selectedTabKey,
        ],
    );

    // trackingContext changes with selectedTabKey (target_entity_types); read it from a ref
    // so switching tabs doesn't re-fire this "opened" event.
    const trackingContextRef = useRef(trackingContext);
    trackingContextRef.current = trackingContext;

    useEffect(() => {
        sendEvent('search_opened', trackingContextRef.current);
    }, [sendEvent]);

    useEffect(() => {
        if (!dataUpdatedAt || !search) {
            return;
        }

        sendEvent('search_request', {
            ...trackingContext,
            version,
            request_ids: requestIds,
            search_query: search,
            limit,
            offset,
            results: [...dataInOrder.entries()].flatMap(([entity, items]) =>
                items.map<SearchResultItem>((item) => ({
                    entity_type: entity,
                    entity_id: item.id,
                    result_id: item.original.result_id,
                    match_type: item.original.match_type,
                })),
            ),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per unique response
    }, [sendEvent, dataUpdatedAt, trackingContext]);

    const trackResultClicked = useCallback(
        (item: SearchEntityItem) => {
            if (!dataUpdatedAt) {
                return;
            }

            sendEvent('search_result_clicked', {
                ...trackingContext,
                request_id: requestIds?.[item.entity],
                result_entity_type: item.entity,
                result_entity_id: item.id,
                result_id: item.original.result_id,
                version,
                extra: {
                    match_type: item.original.match_type,
                    match_reasons:
                        item.original.entity === SearchEntity.Customer
                            ? item.original.match_reasons
                            : undefined,
                },
            });
        },
        [sendEvent, dataUpdatedAt, trackingContext, requestIds, version],
    );

    return {
        data: dataInOrder,
        version,
        requestIds,
        dataUpdatedAt,
        limit,
        offset,
        isLoading: isLoadingQuery && Boolean(search),
        isFetched: isFetchedQuery,
        trackResultClicked,
    };
}

type SearchEntityNavigationItem = { erp_id: string; entity: SearchEntity };

type UseSearchEntityNavigationReturn = {
    navigateToEntity: (item: SearchEntityNavigationItem) => void;
};

export function useSearchEntityNavigation(): UseSearchEntityNavigationReturn {
    const navigate = useNavigate();
    const { openProductDetails, openOrderQuoteDetails } = useDialogs();

    const navigateToEntity = useCallback(
        (item: SearchEntityNavigationItem) => {
            const { entity, erp_id } = item;

            switch (entity) {
                case SearchEntity.Customer:
                    void navigate(`/customers/${encodeURIComponent(erp_id)}`);
                    break;
                case SearchEntity.Product:
                    openProductDetails(erp_id);
                    break;
                case SearchEntity.SalesOrder:
                case SearchEntity.SalesQuote:
                    openOrderQuoteDetails(
                        entity === SearchEntity.SalesOrder ? SrSalesOrderType.ORDER : SrSalesOrderType.QUOTE,
                        erp_id,
                    );
                    break;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return {
        navigateToEntity,
    };
}
